import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { analytics, search } from '../services/api';

const AppContext = createContext();

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within AppProvider');
  }
  return context;
};

// Normalize operator stats from various API response formats
const normalizeStats = (data) => {
  if (!data) return null;

  const operators = ['africel', 'vodacom', 'orange', 'airtel'];
  const result = {};

  // Handle array format
  const entries = data.by_operator || data.operators || data.data?.operators;
  if (Array.isArray(entries)) {
    entries.forEach((entry) => {
      const key = (entry.operator || entry.name || '').toLowerCase().replace('africell', 'africel');
      if (operators.includes(key)) {
        result[key] = {
          total_records: entry.total_records || entry.total || 0,
          today_records: entry.today_records || entry.today || 0,
          status: entry.status || 'online',
        };
      }
    });
  }

  // Handle object format
  operators.forEach((op) => {
    if (!result[op] && data[op]) {
      result[op] = {
        total_records: data[op].total_records || data[op].total || 0,
        today_records: data[op].today_records || 0,
        status: data[op].status || 'online',
      };
    }
    if (!result[op]) {
      result[op] = { total_records: 0, today_records: 0, status: 'unknown' };
    }
  });

  return result;
};

export const AppProvider = ({ children }) => {
  const [stats, setStats] = useState(null);
  const [globalSummary, setGlobalSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedOperator, setSelectedOperator] = useState('all');

  const loadStats = useCallback(async () => {
    try {
      const [analyticsRes, unifiedRes] = await Promise.all([
        analytics.operatorStats().catch(() => null),
        search.unifiedStats().catch(() => null),
      ]);

      const analyticsData = analyticsRes?.data?.data || analyticsRes?.data;
      const unifiedData = unifiedRes?.data?.data || unifiedRes?.data;

      const normalized = normalizeStats(analyticsData) || normalizeStats(unifiedData);
      setStats(normalized);
      setGlobalSummary(analyticsData || unifiedData);
      setError(null);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStats();
    const interval = setInterval(loadStats, 30000); // Refresh every 30s
    return () => clearInterval(interval);
  }, [loadStats]);

  const value = {
    stats,
    globalSummary,
    loading,
    error,
    selectedOperator,
    setSelectedOperator,
    loadStats,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};
